/**
 * Short tones for the workout timers.
 *
 * Synthesised with the Web Audio API rather than shipped as audio files: three beeps would otherwise
 * mean three network requests and three assets to cache, for a few hundred milliseconds of sine wave.
 *
 * Everything here is best-effort and silent on failure. Audio is a nice-to-have on a training screen --
 * a browser that blocks it, or a device with no output, must not break the session. Every call is
 * wrapped rather than trusted.
 */

let context: AudioContext | null = null;

/**
 * Browsers refuse to create or resume an AudioContext until the user has interacted with the page, so
 * this is created lazily on the first beep -- which always follows a tap ("Start workout").
 */
function getContext(): AudioContext | null {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!context) context = new Ctor();
    // Suspended is the normal state after an autoplay block; resuming inside a user gesture is allowed.
    if (context.state === "suspended") void context.resume();
    return context;
  } catch {
    return null;
  }
}

function tone(frequency: number, durationMs: number, volume = 0.15): void {
  const ctx = getContext();
  if (!ctx) return;
  try {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    gain.gain.value = volume;
    oscillator.connect(gain).connect(ctx.destination);

    const now = ctx.currentTime;
    // Ramped rather than cut, because an abruptly stopped oscillator clicks.
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
    oscillator.start(now);
    oscillator.stop(now + durationMs / 1000);
  } catch {
    // Never let a missing speaker end a workout.
  }
}

/** Each tick of a 3-2-1 countdown. Lower and shorter than the go tone, so the last one lands as an event. */
export function playTick(): void {
  tone(660, 120);
}

/** Go. Higher and longer than a tick -- the difference is what makes a countdown feel like it resolves. */
export function playGo(): void {
  tone(990, 260, 0.2);
}

/** A rest or hold timer reaching zero, when the athlete may well not be looking at the screen. */
export function playTimerEnd(): void {
  tone(880, 180);
  setTimeout(() => tone(1180, 260, 0.2), 200);
}

/** The whole session finished. Three rising notes, unmistakably an ending rather than another interval. */
export function playFanfare(): void {
  tone(660, 160);
  setTimeout(() => tone(880, 160), 170);
  setTimeout(() => tone(1320, 380, 0.2), 340);
}
